import { cn } from '../../cn/cn';

export type AlertColor = 'error' | 'warning' | 'info' | 'success';
export type AlertVariant = 'outline' | 'solid';

/**
 * As with Button, colour is custom properties the variant reads.
 *
 * `subtle` and `strong` (ramp 50 behind ramp 800) exist in appTailwind.css for
 * the soft alert, the one place a colour is both background and ink. They are
 * written out, not derived from `main`, so their contrast was checked.
 */
const COLORS: Record<AlertColor, string> = {
  error:
    '[--alert-main:var(--color-error)] [--alert-subtle:var(--color-error-subtle)]' +
    ' [--alert-strong:var(--color-error-strong)]' +
    ' [--alert-contrast:var(--color-error-contrast)]',
  warning:
    '[--alert-main:var(--color-warning)] [--alert-subtle:var(--color-warning-subtle)]' +
    ' [--alert-strong:var(--color-warning-strong)]' +
    ' [--alert-contrast:var(--color-warning-contrast)]',
  info:
    '[--alert-main:var(--color-info)] [--alert-subtle:var(--color-info-subtle)]' +
    ' [--alert-strong:var(--color-info-strong)]' +
    ' [--alert-contrast:var(--color-info-contrast)]',
  success:
    '[--alert-main:var(--color-success)] [--alert-subtle:var(--color-success-subtle)]' +
    ' [--alert-strong:var(--color-success-strong)]' +
    ' [--alert-contrast:var(--color-success-contrast)]',
};

/**
 * The soft alert is the base, since nearly every call site wants it. The
 * variants override it through tailwind-merge.
 *
 * `--alert-icon` is a variable because the icon is a child element a variant
 * class cannot reach.
 */
const BASE =
  'flex items-start gap-3 box-border rounded-md border-0 px-4 py-3' +
  ' font-sans text-sm bg-(--alert-subtle) text-(--alert-strong)' +
  ' [--alert-icon:var(--alert-main)]';

const VARIANTS: Record<AlertVariant, string> = {
  outline: 'border border-(--alert-main)/40 bg-transparent',
  solid:
    'bg-(--alert-main) text-(--alert-contrast) [--alert-icon:currentColor]',
};

/**
 * The three slots. The icon takes the colour's main, or the text colour on a
 * solid alert, and sits on the message's first line rather than centred.
 */
export const ALERT_ICON_CLASSES =
  'flex shrink-0 text-(--alert-icon) [&_svg]:size-5';

export const ALERT_MESSAGE_CLASSES = 'min-w-0 flex-1 overflow-auto';

/** The bold first line. No colour of its own; it inherits the message ink. */
export const ALERT_TITLE_CLASSES = 'mb-0.5 font-semibold';
export const ALERT_ACTION_CLASSES = 'flex shrink-0 items-start -mr-2 -my-1';

export const alertClasses = ({
  color,
  variant,
  className,
}: {
  color: AlertColor;
  variant?: AlertVariant;
  className?: string;
}): string =>
  cn(
    BASE,
    COLORS[color],
    variant !== undefined && VARIANTS[variant],
    className,
  );
